import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  Autocomplete,
  InputAdornment,
  IconButton,
  Snackbar,
  Alert,
  Tooltip,
  LinearProgress,
  Fade,
  Menu,
  MenuItem,
  Chip,
  Grid,
} from "@mui/material";
import axios from "axios";
import { AutoTaskAuthContext } from "../Context/AutoTaskContext";
import RaiseServiceTicket from "./RaiseServiceTicket";
import LoadingScreen from "./LoadingScreen";

import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import NewReleasesRoundedIcon from "@mui/icons-material/NewReleasesRounded";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import BugReportRoundedIcon from "@mui/icons-material/BugReportRounded";
import PendingActionsRoundedIcon from "@mui/icons-material/PendingActionsRounded";
import ListAltRoundedIcon from "@mui/icons-material/ListAltRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";

const BASE = process.env.REACT_APP_API_BASE_URL;

// ── Grid template — added 160px Test Cases column after Screen ────────────────
const GRID_COLS = "150px 1fr 160px 90px 130px 130px 110px 110px 100px";

// ── Status config ──────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  pending: { label: "Pending", color: "#6B7280", bg: "#F3F4F6" },
  in_progress: { label: "In Progress", color: "#D97706", bg: "#FFFBEB" },
  testing: { label: "Testing", color: "#0891B2", bg: "#ECFEFF" },
  completed: { label: "Completed", color: "#7C3AED", bg: "#F5F3FF" },
};

const deriveStatus = (task) => {
  if (task.tester_completion_date) return "completed";
  if (task.owner_completion_date) return "testing";
  if (task.ticket_number) return "in_progress";
  return "pending";
};

// ── Shared TextField style — hex inline ───────────────────────────────────────
const dialogFieldSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "#FFFFFF",
    borderRadius: "10px",
    color: "#111827",
    fontSize: 16,
    "& fieldset": { borderColor: "#E5E7EB" },
    "&:hover fieldset": { borderColor: "#9CA3AF" },
    "&.Mui-focused fieldset": { borderColor: "#4F46E5" },
  },
  "& .MuiInputLabel-root": { color: "#9CA3AF", fontSize: 13 },
  "& .MuiInputLabel-root.Mui-focused": { color: "#4F46E5" },
  "& .MuiSvgIcon-root": { color: "#9CA3AF" },
  "& .MuiFormHelperText-root": { color: "#EF4444", fontSize: 11 },
};

// ── Status chip ────────────────────────────────────────────────────────────────
const statusChip = (derivedStatus) => {
  const cfg = STATUS_CONFIG[derivedStatus] || STATUS_CONFIG.pending;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 1.25,
        py: 0.3,
        borderRadius: "6px",
        bgcolor: cfg.bg,
        color: cfg.color,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.4,
        fontFamily: "'DM Mono', monospace",
        whiteSpace: "nowrap",
        border: `1px solid ${cfg.color}33`,
      }}
    >
      {cfg.label.toUpperCase()}
    </Box>
  );
};

// ── Avatar ─────────────────────────────────────────────────────────────────────
const AVATAR_COLORS = [
  "#4F46E5",
  "#0891B2",
  "#D97706",
  "#DC2626",
  "#2563EB",
  "#DB2777",
];

const avatar = (name, size = 26) => {
  if (!name) return null;
  const initials = name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const col = AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        bgcolor: col + "18",
        border: `1.5px solid ${col}44`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size * 0.36,
        fontWeight: 700,
        color: col,
        fontFamily: "'DM Mono', monospace",
        flexShrink: 0,
      }}
    >
      {initials}
    </Box>
  );
};

// ── Project Statuses ─────────────────────────────────────────────────────────────────────
const STATUS_TABS = [
  {
    key: "all",
    label: "All",
    icon: ListAltRoundedIcon,
    color: "#4F46E5",
    bg: "#EEF2FF",
  },
  {
    key: "unapproved",
    label: "Unapproved",
    icon: NewReleasesRoundedIcon,
    color: "#DC2626",
    bg: "#FEF2F2",
  },
  {
    key: "approved",
    label: "Approved",
    icon: DoneAllRoundedIcon,
    color: "#16A34A",
    bg: "#F0FDF4",
  },
  {
    key: "pending",
    label: "Pending",
    icon: PendingActionsRoundedIcon,
    color: "#64748B",
    bg: "#F1F5F9",
  },
  {
    key: "in_progress",
    label: "In Progress",
    icon: BoltRoundedIcon,
    color: "#D97706",
    bg: "#FFFBEB",
  },
  {
    key: "testing",
    label: "Testing",
    icon: BugReportRoundedIcon,
    color: "#0891B2",
    bg: "#ECFEFF",
  },
  {
    key: "completed",
    label: "Completed",
    icon: VerifiedRoundedIcon,
    color: "#7C3AED",
    bg: "#F5F3FF",
  },
];

// ══════════════════════════════════════════════════════════════════════════════
// TASK ROW
// ══════════════════════════════════════════════════════════════════════════════
const TaskRow = ({
  task,
  index,
  onEdit,
  onDelete,
  onCreateTicket,
  onApprove,
  onComplete,
  userInfo,
}) => {
  const [anchor, setAnchor] = useState(null);
  const [navigating, setNavigating] = useState(false);
  const navigate = useNavigate();

  const hasTicket = !!task.ticket_number;
  const isApproved = !!task.approved_date;
  const isCompleted = !!task.tester_completion_date;
  const canApprove = !isApproved;
  const canComplete = !isCompleted;
  const derived = deriveStatus(task);

  // Split unit_test by "/" — each item becomes its own line
  const testCases = task.unit_test
    ? task.unit_test
        .split("/")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

  // Split description by "/" — each item becomes its own line
  const descriptionLines = task.description
    ? task.description
        .split("/")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

  const handleTicketClick = async () => {
    if (!task.ticket_number || navigating) return;
    setNavigating(true);
    try {
      const res = await axios.get(
        `${BASE}/api/ticket/ticket-by-number/${encodeURIComponent(task.ticket_number)}`,
        { params: { pm_id: task.pm_id } },
      );
      if (res.data.success)
        navigate("/ticket-panel", { state: { data: res.data.data } });
    } catch (err) {
      console.error("Failed to navigate to ticket:", err);
    } finally {
      setNavigating(false);
    }
  };

  return (
    <Box
      sx={{
        bgcolor: isApproved ? "#F0FDF4" : "#FFFFFF",
        border: isApproved ? "1px solid #86EFAC" : "1px solid #E5E7EB",
        borderRadius: "12px",
        px: 2,
        py: 1.5,
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: GRID_COLS,
        },
        gap: 1,
        alignItems: "start",
        animation: `slideIn 0.2s ease ${index * 0.04}s both`,
        transition: "border-color 0.15s, box-shadow 0.15s",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        "&:hover": {
          borderColor: isApproved ? "#4ADE80" : "#9CA3AF",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        },
      }}
    >
      {/* PM ID */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, pt: 0.3 }}>
        {isApproved && (
          <Tooltip
            title={`Approved${
              task.approved_by_name ? " by " + task.approved_by_name : ""
            }`}
            arrow
          >
            <VerifiedOutlinedIcon
              sx={{ fontSize: 16, color: "#16A34A", flexShrink: 0 }}
            />
          </Tooltip>
        )}
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: "#4F46E5",
            fontFamily: "'DM Mono', monospace",
          }}
        >
          {task.pm_id}
        </Typography>
      </Box>

      {/* Screen - Updated with multi-line description support */}
      <Box>
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 600,
            color: "#111827",
            lineHeight: 1.3,
            mb: descriptionLines.length > 0 ? 0.5 : 0,
          }}
        >
          {task.screen_name}
        </Typography>
        {descriptionLines.length > 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
            {descriptionLines.map((line, idx) => (
              <Typography
                key={idx}
                sx={{
                  fontSize: 14,
                  color: "#6B7280",
                  lineHeight: 1.45,
                }}
              >
                {line}
              </Typography>
            ))}
          </Box>
        )}
      </Box>

      {/* Test Cases — each slash-separated entry on its own line */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6 }}>
        {testCases.length > 0 ? (
          testCases.map((tc, idx) => (
            <Box
              key={idx}
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              <BugReportOutlinedIcon
                sx={{
                  fontSize: 14,
                  color: "#16A34A",
                  mt: "1px",
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: 14,
                  color: "#374151",
                  fontWeight: 500,
                  lineHeight: 1.45,
                }}
              >
                {tc}
              </Typography>
            </Box>
          ))
        ) : (
          <Typography sx={{ fontSize: 14, color: "#D1D5DB", pt: 0.3 }}>
            --
          </Typography>
        )}
      </Box>

      {/* Budget */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, pt: 0.3 }}>
        <TimerOutlinedIcon sx={{ fontSize: 14, color: "#9CA3AF" }} />
        <Typography
          sx={{
            fontSize: 14,
            color: "#6B7280",
            fontFamily: "'DM Mono', monospace",
          }}
        >
          {task.time_budget}m
        </Typography>
      </Box>

      {/* Owner */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, pt: 0.3 }}>
        {task.owner_name ? (
          <>
            {avatar(task.owner_name, 22)}
            <Typography sx={{ fontSize: 14, color: "#6B7280" }}>
              {task.owner_name.split(" ")[0]}
            </Typography>
          </>
        ) : (
          <Typography sx={{ fontSize: 14, color: "#D1D5DB" }}>--</Typography>
        )}
      </Box>

      {/* Tester */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, pt: 0.3 }}>
        {task.tester_name ? (
          <>
            {avatar(task.tester_name, 22)}
            <Typography sx={{ fontSize: 14, color: "#6B7280" }}>
              {task.tester_name.split(" ")[0]}
            </Typography>
          </>
        ) : (
          <Typography sx={{ fontSize: 14, color: "#D1D5DB" }}>--</Typography>
        )}
      </Box>

      {/* Status chip */}
      <Box sx={{ pt: 0.3 }}>{statusChip(derived)}</Box>

      {/* Ticket */}
      <Box sx={{ pt: 0.3 }}>
        {hasTicket ? (
          <Tooltip title="View ticket" arrow>
            <Box
              onClick={handleTicketClick}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                bgcolor: navigating ? "#F3F4F6" : "#ECFEFF",
                border: "1px solid #A5F3FC",
                borderRadius: "8px",
                px: 1,
                py: 0.4,
                cursor: navigating ? "wait" : "pointer",
                transition: "all 0.15s",
                "&:hover": { bgcolor: "#CFFAFE", borderColor: "#0891B2" },
              }}
            >
              <ConfirmationNumberOutlinedIcon
                sx={{ fontSize: 14, color: "#0891B2" }}
              />
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#0891B2",
                  fontFamily: "'DM Mono', monospace",
                }}
              >
                {task.ticket_number}
              </Typography>
            </Box>
          </Tooltip>
        ) : (
          <Tooltip title="Create ticket for this task" arrow>
            <Box
              onClick={onCreateTicket}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                bgcolor: "#F9FAFB",
                border: "1px dashed #D1D5DB",
                borderRadius: "8px",
                px: 1,
                py: 0.4,
                cursor: "pointer",
                transition: "all 0.15s",
                "&:hover": {
                  borderColor: "#4F46E5",
                  bgcolor: "#EEF2FF",
                  "& .pm-plus": { color: "#4F46E5" },
                },
              }}
            >
              <AddRoundedIcon
                className="pm-plus"
                sx={{ fontSize: 14, color: "#9CA3AF" }}
              />
              <Typography
                className="pm-plus"
                sx={{ fontSize: 14, color: "#9CA3AF", fontWeight: 600 }}
              >
                Ticket
              </Typography>
            </Box>
          </Tooltip>
        )}
      </Box>

      {/* Actions */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 0.5,
          pt: 0.3,
        }}
      >
        {canApprove && (
          <Tooltip title="Approve this task" arrow>
            <Box
              onClick={() => onApprove(task.pm_id)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                bgcolor: "#F0FDF4",
                border: "1px solid #86EFAC",
                borderRadius: "6px",
                px: 0.9,
                py: 0.3,
                cursor: "pointer",
                transition: "all 0.15s",
                "&:hover": { bgcolor: "#DCFCE7", borderColor: "#16A34A" },
              }}
            >
              <TaskAltRoundedIcon sx={{ fontSize: 14, color: "#16A34A" }} />
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#16A34A",
                  fontFamily: "'DM Mono', monospace",
                }}
              >
                Approve
              </Typography>
            </Box>
          </Tooltip>
        )}

        <IconButton
          size="small"
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{
            color: "#9CA3AF",
            "&:hover": { color: "#374151", bgcolor: "#F3F4F6" },
          }}
        >
          <MoreVertRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>

        <Menu
          anchorEl={anchor}
          open={Boolean(anchor)}
          onClose={() => setAnchor(null)}
          PaperProps={{
            sx: {
              bgcolor: "#FFFFFF",
              border: "1px solid #E5E7EB",
              borderRadius: "10px",
              minWidth: 150,
              boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            },
          }}
        >
          <MenuItem
            onClick={() => {
              setAnchor(null);
              onEdit();
            }}
            sx={{
              fontSize: 14,
              color: "#6B7280",
              gap: 1,
              "&:hover": { bgcolor: "#F9FAFB", color: "#111827" },
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: 16 }} /> Edit
          </MenuItem>

          {!hasTicket && (
            <MenuItem
              onClick={() => {
                setAnchor(null);
                onCreateTicket();
              }}
              sx={{
                fontSize: 14,
                color: "#6B7280",
                gap: 1,
                "&:hover": { bgcolor: "#F9FAFB", color: "#111827" },
              }}
            >
              <ConfirmationNumberOutlinedIcon sx={{ fontSize: 16 }} /> Create
              Ticket
            </MenuItem>
          )}

          {canApprove && (
            <MenuItem
              onClick={() => {
                setAnchor(null);
                onApprove(task.pm_id);
              }}
              sx={{
                fontSize: 14,
                color: "#16A34A",
                gap: 1,
                "&:hover": { bgcolor: "#F0FDF4" },
              }}
            >
              <VerifiedOutlinedIcon sx={{ fontSize: 16 }} /> Approve
            </MenuItem>
          )}

          {canComplete && (
            <MenuItem
              onClick={() => {
                setAnchor(null);
                onComplete(task.pm_id);
              }}
              sx={{
                fontSize: 14,
                color: "#7C3AED",
                gap: 1,
                "&:hover": { bgcolor: "#F5F3FF" },
              }}
            >
              <TaskAltRoundedIcon sx={{ fontSize: 16 }} /> Mark Complete
            </MenuItem>
          )}

          <MenuItem
            onClick={() => {
              setAnchor(null);
              onDelete();
            }}
            sx={{
              fontSize: 14,
              color: "#DC2626",
              gap: 1,
              "&:hover": { bgcolor: "#FEF2F2" },
            }}
          >
            <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} /> Delete
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

// ── Completion badge ───────────────────────────────────────────────────────────
const CompletionBadge = ({ icon, label, date, timeTaken, color }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 0.75,
      bgcolor: color + "11",
      border: `1px solid ${color}30`,
      borderRadius: "6px",
      px: 1,
      py: 0.3,
    }}
  >
    <Box sx={{ color, display: "flex" }}>{icon}</Box>
    <Typography sx={{ fontSize: 11, color, fontWeight: 600 }}>
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: 11,
        color: "#9CA3AF",
        fontFamily: "'DM Mono', monospace",
      }}
    >
      {new Date(date).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
      })}
    </Typography>
    {timeTaken && (
      <Typography
        sx={{
          fontSize: 11,
          color: "#9CA3AF",
          fontFamily: "'DM Mono', monospace",
        }}
      >
        · {timeTaken}m
      </Typography>
    )}
  </Box>
);

// ══════════════════════════════════════════════════════════════════════════════
// CREATE / EDIT TASK DIALOG
// ══════════════════════════════════════════════════════════════════════════════
const TaskDialog = ({
  open,
  onClose,
  editData,
  projects,
  allUsers,
  userInfo,
  onSaved,
  showSnack,
  selectedProject,
  initialScreenName = "", // NEW: persisted screen name
  onScreenNameChange, // NEW: callback to persist screen name
}) => {
  const isEdit = !!editData;

  // Both Owner and Tester now default to the logged‑in user
  const blank = {
    store_id: userInfo?.store_id ?? "",
    project_id: "",
    screen_name: "",
    description: "",
    unit_test: "",
    time_budget: "",
    owner_id: userInfo?.id ?? "", // changed
    tester_id: userInfo?.id ?? "", // changed
    status: "pending",
  };

  const [form, setForm] = useState(blank);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Latest persisted screen name. Read only when the dialog opens, so that
  // typing in the Screen / Task Name field can never re-trigger the reset
  // effect below and wipe the rest of the form.
  const initialScreenNameRef = useRef(initialScreenName);
  initialScreenNameRef.current = initialScreenName;

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setForm(
      isEdit
        ? {
            store_id: editData.store_id ?? userInfo?.store_id ?? "",
            project_id: editData.project_id || "",
            screen_name: editData.screen_name || "",
            description: editData.description || "",
            unit_test: editData.unit_test || "",
            time_budget: editData.time_budget || "",
            owner_id: editData.owner_id || "",
            tester_id: editData.tester_id || "",
            status: editData.pm_status || "pending",
          }
        : {
            ...blank,
            store_id: userInfo?.store_id ?? "",
            project_id: selectedProject?.id || "",
            screen_name: initialScreenNameRef.current || "", // use persisted name if available
          },
    );
    // `initialScreenName` is intentionally NOT a dependency: it changes on every
    // keystroke in the Screen / Task Name field, and re-running this effect would
    // reset the form and silently wipe everything already filled in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editData]);

  const validate = () => {
    const e = {};
    // if (!form.project_id) e.project_id = "Required";
    if (!form.screen_name?.trim()) e.screen_name = "Required";
    if (!form.time_budget) e.time_budget = "Required";
    if (parseInt(form.time_budget) > 80)
      e.time_budget = "Must be less than or equal to 80 mins";
    if (parseInt(form.time_budget) < 1)
      e.time_budget = "Must be at least 1 min";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        store_id: form.store_id ?? userInfo?.store_id,
        project_id: parseInt(form.project_id),
        screen_name: form.screen_name.trim(),
        description: form.description || null,
        unit_test: form.unit_test || null,
        time_budget: parseInt(form.time_budget),
        owner_id: form.owner_id || null,
        tester_id: form.tester_id || null,
        status: form.status,
      };
      const res = isEdit
        ? await axios.put(
            `${BASE}/api/ticket/project-management/${encodeURIComponent(
              editData.pm_id,
            )}`,
            payload,
          )
        : await axios.post(`${BASE}/api/ticket/project-management`, payload);
      if (res.data.success) {
        onSaved(res.data.data, isEdit);
      } else {
        showSnack(res.data.statusMessage || "Failed", "error");
      }
    } catch {
      showSnack("Something went wrong", "error");
    } finally {
      setSaving(false);
    }
  };

  const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const STATUS_EDIT_OPTIONS = [
    { value: "pending", label: "Pending" },
    { value: "in_progress", label: "In Progress" },
    { value: "testing", label: "Testing" },
    { value: "completed", label: "Completed" },
  ];

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        onClose();
      }}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#FFFFFF",
          border: "1px solid #E5E7EB",
          borderRadius: "16px",
          backgroundImage: "none",
          boxShadow: "0 8px 32px rgba(0,0,0,0.10)",

          // Responsive dialog width
          width: {
            xs: "calc(100% - 32px)", // Mobile
            sm: "600px", // Tablet
            md: "720px", // Laptop
            lg: "780px", // Desktop
            xl: "820px", // Large desktop
          },

          maxWidth: "calc(100% - 32px)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          p: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            flexDirection: "column",
          }}
        >
          <Chip color="info" label={`Project - ${selectedProject?.name}`} />

          <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#111827" }}>
            {isEdit ? `Edit — ${editData?.pm_id}` : "New Project Task"}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{ color: "#db1f1f", "&:hover": { bgcolor: "#e6e6e6" } }}
        >
          <CloseRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <Box
        sx={{
          px: 2.5,
          pb: 2.5,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <TextField
          label="Screen / Task Name *"
          size="small"
          fullWidth
          value={form.screen_name}
          onChange={(e) => {
            set("screen_name")(e);
            // Only a brand-new task seeds the next one; editing an existing task
            // must not touch the persisted name.
            if (!isEdit) onScreenNameChange?.(e.target.value);
          }}
          error={!!errors.screen_name}
          helperText={errors.screen_name}
          sx={dialogFieldSx}
        />

        <TextField
          label="Description"
          size="small"
          fullWidth
          multiline
          minRows={2}
          value={form.description}
          onChange={set("description")}
          sx={dialogFieldSx}
        />

        <TextField
          label="Unit Test (separate multiple test cases with /)"
          size="small"
          fullWidth
          multiline
          minRows={2}
          value={form.unit_test}
          onChange={set("unit_test")}
          sx={dialogFieldSx}
        />

        <Grid
          container
          justifyContent="space-between"
          columnSpacing={{ xs: 0, sm: 0, md: 1 }}
          rowSpacing={2}
        >
          <Grid item md={2.25} sm={12} xs={12}>
            <TextField
              label="Time Budget (max 80) *"
              type="text"
              size="small"
              fullWidth
              value={form.time_budget}
              onChange={(e) => {
                // Allow only digits
                const value = e.target.value.replace(/\D/g, "");

                setForm((p) => ({
                  ...p,
                  time_budget: value,
                }));
              }}
              error={!!errors.time_budget}
              helperText={errors.time_budget}
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
                maxLength: 2,
              }}
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "#9CA3AF",
                        whiteSpace: "nowrap",
                      }}
                    >
                      mins
                    </Typography>
                  </InputAdornment>
                ),
              }}
              sx={dialogFieldSx}
            />
          </Grid>

          <Grid item md={4.75} sm={12} xs={12}>
            <Autocomplete
              options={allUsers}
              getOptionLabel={(o) => o.name || ""}
              value={allUsers.find((u) => u.id === form.owner_id) || null}
              onChange={(_, v) =>
                setForm((p) => ({ ...p, owner_id: v?.id || "" }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Owner (Developer)"
                  size="small"
                  sx={dialogFieldSx}
                />
              )}
            />
          </Grid>

          <Grid item md={4.75} sm={12} xs={12}>
            <Autocomplete
              options={allUsers}
              getOptionLabel={(o) => o.name || ""}
              value={allUsers.find((u) => u.id === form.tester_id) || null}
              onChange={(_, v) =>
                setForm((p) => ({ ...p, tester_id: v?.id || "" }))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tester (QA)"
                  size="small"
                  sx={dialogFieldSx}
                />
              )}
            />
          </Grid>
        </Grid>

        {isEdit && (
          <Autocomplete
            disabled
            options={STATUS_EDIT_OPTIONS}
            getOptionLabel={(o) => o.label}
            value={
              STATUS_EDIT_OPTIONS.find((o) => o.value === form.status) || null
            }
            onChange={(_, v) =>
              setForm((p) => ({ ...p, status: v?.value || "pending" }))
            }
            renderInput={(params) => (
              <TextField
                disabled
                {...params}
                label="Status"
                size="small"
                sx={dialogFieldSx}
              />
            )}
          />
        )}

        <Button
          onClick={handleSave}
          disabled={saving}
          fullWidth
          sx={{
            bgcolor: "#4F46E5",
            color: "#FFFFFF",
            borderRadius: "10px",
            py: 1.1,
            fontSize: 14,
            fontWeight: 600,
            textTransform: "none",
            mt: 0.5,
            boxShadow: "0 2px 10px #4F46E530",
            "&:hover": { bgcolor: "#4338CA" },
            "&:disabled": { bgcolor: "#E5E7EB", color: "#9CA3AF" },
          }}
        >
          {saving ? "Saving…" : isEdit ? "Update Task" : "Create Task"}
        </Button>
      </Box>
    </Dialog>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// TICKET FORM DIALOG
// ══════════════════════════════════════════════════════════════════════════════
const TicketFormDialog = ({
  open,
  onClose,
  pmTask,
  userInfo,
  onCreated,
  showSnack,
}) => {
  if (!pmTask) return null;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      BackdropProps={{
        sx: { backdropFilter: "blur(6px)", backgroundColor: "rgba(0,0,0,0.2)" },
      }}
      PaperProps={{
        sx: {
          borderRadius: "16px",
          overflow: "hidden",
          maxHeight: "90vh",
          backgroundImage: "none",
          boxShadow: "0 8px 32px rgba(0,0,0,0.10)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          px: 2.5,
          pt: 2,
          pb: 1.5,
          borderBottom: "1px solid #E5E7EB",
          bgcolor: "#FFFFFF",
          flexShrink: 0,
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#111827" }}>
            Create Ticket
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.4 }}>
            <Typography
              sx={{
                fontSize: 11,
                color: "#4F46E5",
                fontFamily: "'DM Mono', monospace",
                bgcolor: "#EEF2FF",
                border: "1px solid #C7D2FE",
                px: 0.75,
                py: 0.2,
                borderRadius: "4px",
              }}
            >
              {pmTask.pm_id}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
              {pmTask.screen_name}
            </Typography>
          </Box>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            color: "#9CA3AF",
            "&:hover": { color: "#111827", bgcolor: "#F3F4F6" },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <Box sx={{ overflowY: "auto", flex: 1 }}>
        <RaiseServiceTicket
          deptId={pmTask.project_id}
          storeId={pmTask.store_id}
          pmTask={pmTask}
          isDialogMode={true}
          onTicketCreated={(ticketData) => onCreated(pmTask.pm_id, ticketData)}
          onClose={onClose}
          showSnack={showSnack}
        />
      </Box>
    </Dialog>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
const ProjectManagement = () => {
  const { allUsers, userInfo } = useContext(AutoTaskAuthContext);
  const store_id = userInfo?.store_id;

  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [taskDialog, setTaskDialog] = useState(false);
  const [ticketDialog, setTicketDialog] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [activeTask, setActiveTask] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });

  // ── NEW: screen name persistence ────────────────────────────────────────
  const [persistedScreenName, setPersistedScreenName] = useState("");
  const [persistedProjectId, setPersistedProjectId] = useState(null);

  // Reset persisted screen name when the selected project changes
  useEffect(() => {
    if (!selectedProject) {
      setPersistedScreenName("");
      setPersistedProjectId(null);
    } else if (selectedProject.id !== persistedProjectId) {
      setPersistedScreenName("");
      setPersistedProjectId(selectedProject.id);
    }
  }, [selectedProject, persistedProjectId]);

  const showSnack = (msg, sev = "success") =>
    setSnack({ open: true, msg, sev });

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${BASE}/api/ticket/projects/${store_id}`, {
          params: { store_id: userInfo?.store_id },
        });

        const projectList = res.data.data || [];
        setProjects(projectList);

        // ✅ Auto-select last project if available
        if (projectList.length > 0) {
          setSelectedProject(projectList[0]);
        }
      } catch {
        /* silent */
      }
    })();
  }, [userInfo?.store_id]);

  useEffect(() => {
    if (!selectedProject) {
      setTasks([]);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${BASE}/api/ticket/projects/tasks/${selectedProject.id}/${store_id}`,
        );
        setTasks(res.data.data || []);
      } catch {
        showSnack("Failed to fetch tasks", "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedProject]);

  const filtered = tasks.filter((task) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      task.screen_name?.toLowerCase().includes(q) ||
      task.pm_id?.toLowerCase().includes(q) ||
      task.owner_name?.toLowerCase().includes(q);
    const derived = deriveStatus(task);
    const matchStatus =
      filterStatus === "all"
        ? true
        : filterStatus === "approved"
        ? !!task.approved_date
        : filterStatus === "unapproved"
        ? !task.approved_date
        : derived === filterStatus;
    return matchSearch && matchStatus;
  });

  const stats = {
    total: tasks.length,
    unapproved: tasks.filter((t) => !t.approved_date).length,
    approved: tasks.filter((t) => !!t.approved_date).length,
    pending: tasks.filter((t) => deriveStatus(t) === "pending").length,
    in_progress: tasks.filter((t) => deriveStatus(t) === "in_progress").length,
    testing: tasks.filter((t) => deriveStatus(t) === "testing").length,
    completed: tasks.filter((t) => deriveStatus(t) === "completed").length,
  };

  const handleTaskSaved = (saved, isEdit) => {
    setTasks((prev) =>
      isEdit
        ? prev.map((t) => (t.pm_id === saved.pm_id ? { ...t, ...saved } : t))
        : [saved, ...prev],
    );
    setTaskDialog(false);
    setEditTask(null);
    showSnack(isEdit ? "Task updated" : "Task created");
  };

  const handleTicketCreated = (pmId, data) => {
    setTasks((prev) =>
      prev.map((t) =>
        t.pm_id === pmId
          ? {
              ...t,
              ticket_id: data.ticket_id,
              ticket_number: data.ticket_number,
            }
          : t,
      ),
    );
    setTicketDialog(false);
    setActiveTask(null);
    showSnack("Ticket created & linked ✓");
  };

  const handleDelete = async (pm_id) => {
    try {
      await axios.post(`${BASE}/api/ticket/project-management/delete`, {
        pm_id,
      });
      setTasks((prev) => prev.filter((t) => t.pm_id !== pm_id));
      showSnack("Task deleted");
    } catch (err) {
      showSnack(
        err?.response?.data?.statusMessage ||
          "Cannot delete — ticket already linked",
        "error",
      );
    }
  };

  const handleApprove = async (pm_id) => {
    try {
      const res = await axios.put(
        `${BASE}/api/ticket/approve-project-task/${encodeURIComponent(pm_id)}`,
        { approved_by: userInfo?.id },
      );
      if (res.data.success) {
        setTasks((prev) =>
          prev.map((t) =>
            t.pm_id === pm_id
              ? {
                  ...t,
                  approved_date: new Date().toISOString(),
                  approved_by: userInfo?.id,
                  approved_by_name: userInfo?.name,
                }
              : t,
          ),
        );
        showSnack("Task approved ✓");
      } else {
        showSnack(res.data.statusMessage || "Failed to approve", "error");
      }
    } catch {
      showSnack("Something went wrong", "error");
    }
  };

  const handleComplete = async (pm_id) => {
    try {
      const res = await axios.put(
        `${BASE}/api/ticket/complete-project-task/${encodeURIComponent(pm_id)}`,
      );
      if (res.data.success) {
        setTasks((prev) =>
          prev.map((t) =>
            t.pm_id === pm_id
              ? { ...t, tester_completion_date: new Date().toISOString() }
              : t,
          ),
        );
        showSnack("Task marked as completed ✓");
      } else {
        showSnack(res.data.statusMessage || "Failed to complete", "error");
      }
    } catch {
      showSnack("Something went wrong", "error");
    }
  };

  const totalFilteredTime = filtered.reduce(
    (sum, t) => sum + (parseInt(t.time_budget) || 0),
    0,
  );

  const getTimeColorConfig = (minutes) => {
    if (minutes < 240) {
      return {
        color: "#DC2626",
        bg: "#FEF2F2",
        border: "#FCA5A5",
      };
    }

    if (minutes <= 360) {
      return {
        color: "#D97706",
        bg: "#FFFBEB",
        border: "#FCD34D",
      };
    }

    return {
      color: "#16A34A",
      bg: "#F0FDF4",
      border: "#86EFAC",
    };
  };

  const timeCfg = getTimeColorConfig(totalFilteredTime);

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#F9FAFB",
        fontFamily: "'DM Sans', sans-serif",
        color: "#111827",
        p: { xs: 2, md: 3 },
        overflow: "hidden",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500;700&display=swap');
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #D1D5DB; border-radius: 4px; }
        @keyframes slideIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
      `}</style>

      {/* STATIC SECTION */}
      <Box sx={{ flexShrink: 0 }}>
        {/* ── Header ── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 1,
            mb: 2,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              bgcolor: "#EEF2FF",
              border: "1px solid #C7D2FE",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AccountTreeRoundedIcon sx={{ fontSize: 24, fill: "#4F46E5" }} />
          </Box>
          <Typography
            sx={{
              fontSize: 28,
              fontWeight: 700,
              letterSpacing: -0.5,
              color: "#111827",
            }}
          >
            Project Management
          </Typography>
        </Box>

        {/* ── Project selector + stat chips ── */}
        <Box
          sx={{
            bgcolor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            borderRadius: "14px",
            p: 2,
            mb: 2.5,
            boxShadow: "0 1px 4px rgba(0,0,0,0.05)",

            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          {/* LEFT SIDE */}
          <Box sx={{ flex: 1, minWidth: 250 }}>
            <Autocomplete
              options={projects}
              getOptionLabel={(o) => o.name || ""}
              value={selectedProject}
              onChange={(_, v) => setSelectedProject(v)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select a project…"
                  size="small"
                  sx={dialogFieldSx}
                />
              )}
            />
          </Box>

          {/* RIGHT SIDE */}
          {selectedProject && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              {[
                { label: "Total", val: stats.total, color: "#374151" },
                { label: "Approved", val: stats.approved, color: "#16A34A" },
                { label: "Pending", val: stats.pending, color: "#6B7280" },
                {
                  label: "In Progress",
                  val: stats.in_progress,
                  color: "#D97706",
                },
                { label: "Testing", val: stats.testing, color: "#0891B2" },
                { label: "Completed", val: stats.completed, color: "#7C3AED" },
              ].map((s) => (
                <Box
                  key={s.label}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.4,
                    bgcolor: s.color + "15",
                    border: `1px solid ${s.color}40`,
                    borderRadius: "20px",
                    px: 1.25,
                    py: 0.4,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 18,
                      fontWeight: 700,
                      color: s.color,
                      fontFamily: "'DM Mono', monospace",
                    }}
                  >
                    {s.val}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 16,
                      color: s.color,
                      opacity: 0.8,
                    }}
                  >
                    {s.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* ── Toolbar ── */}
        {selectedProject && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 2,
              flexWrap: "wrap",
            }}
          >
            <TextField
              size="small"
              placeholder="Search by Name, Project ID, Project Owner"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon
                      sx={{ fontSize: 22, color: "#9CA3AF" }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1, minWidth: 200, ...dialogFieldSx }}
            />

            <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
              {/* TOTAL TIME TAB */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.6,
                  px: 1.5,
                  py: 0.6,
                  borderRadius: "8px",
                  fontSize: 16,
                  fontWeight: 700,
                  bgcolor: timeCfg.bg,
                  color: timeCfg.color,
                  border: `1px solid ${timeCfg.border}`,
                  fontFamily: "'DM Mono', monospace",
                  transition: "all 0.2s ease",
                }}
              >
                <AccessTimeRoundedIcon
                  sx={{
                    fontSize: 16,
                    color: timeCfg.color,
                  }}
                />
                Total: {totalFilteredTime}m
              </Box>

              {/* STATUS FILTER TABS */}
              {STATUS_TABS.map((s) => {
                const Icon = s.icon;
                const active = filterStatus === s.key;

                return (
                  <Box
                    key={s.key}
                    onClick={() => setFilterStatus(s.key)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.6,
                      px: 1.5,
                      py: 0.6,
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontSize: 16,
                      fontWeight: 600,
                      bgcolor: active ? s.bg : "#FFFFFF",
                      color: active ? s.color : "#6B7280",
                      border: active
                        ? `1px solid ${s.color}55`
                        : "1px solid #E5E7EB",
                      transition: "all 0.15s ease",
                      "&:hover": {
                        borderColor: s.color,
                        color: s.color,
                        bgcolor: active ? s.bg : s.bg + "40",
                      },
                    }}
                  >
                    <Icon
                      sx={{
                        fontSize: 16,
                        color: active ? s.color : "#9CA3AF",
                      }}
                    />
                    {s.label}
                  </Box>
                );
              })}
            </Box>

            {/* Add Task Button */}
            <Button
              onClick={() => {
                setEditTask(null);
                setTaskDialog(true);
              }}
              startIcon={<AddRoundedIcon />}
              sx={{
                bgcolor: "#4F46E5",
                color: "#FFFFFF",
                borderRadius: "10px",
                fontSize: 16,
                fontWeight: 600,
                textTransform: "none",
                boxShadow: "0 2px 10px #4F46E530",
                "&:hover": { bgcolor: "#4338CA" },
              }}
            >
              Add Task
            </Button>
          </Box>
        )}
      </Box>

      {/* SCROLLABLE SECTION */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          pr: 1,
        }}
      >
        {/* ── Task list ── */}
        {loading ? (
          <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
            <Box sx={{ width: 200 }}>
              <LinearProgress
                sx={{
                  bgcolor: "#E5E7EB",
                  borderRadius: 4,
                  "& .MuiLinearProgress-bar": { bgcolor: "#4F46E5" },
                }}
              />
            </Box>
          </Box>
        ) : !selectedProject ? (
          <Box
            sx={{
              py: 10,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "18px",
                bgcolor: "#EEF2FF",
                border: "1px solid #C7D2FE",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FolderOutlinedIcon sx={{ fontSize: 32, color: "#4F46E5" }} />
            </Box>
            <Typography sx={{ color: "#6B7280", fontSize: 14 }}>
              Select a project to view its tasks
            </Typography>
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ py: 8, textAlign: "center" }}>
            <Typography sx={{ color: "#9CA3AF", fontSize: 13 }}>
              {search || filterStatus !== "all"
                ? "No tasks match your filters"
                : "No tasks yet — click Add Task"}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {/* Column headers */}
            <Box
              sx={{
                display: { xs: "none", md: "grid" },
                gridTemplateColumns: GRID_COLS,
                px: 2,
                gap: 1,

                position: "sticky",
                top: 0,
                zIndex: 5,
                bgcolor: "#F9FAFB",
                py: 1,
                borderBottom: "1px solid #E5E7EB",
              }}
            >
              {[
                "PM ID",
                "Screen",
                "Test Cases",
                "Budget",
                "Owner",
                "Tester",
                "Status",
                "Ticket",
                "",
              ].map((h) => (
                <Typography
                  key={h}
                  sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#9CA3AF",
                    letterSpacing: 0.8,
                    textTransform: "uppercase",
                    fontFamily: "'DM Mono', monospace",
                  }}
                >
                  {h}
                </Typography>
              ))}
            </Box>

            {filtered.map((task, i) => (
              <TaskRow
                key={task.pm_id}
                task={task}
                index={i}
                userInfo={userInfo}
                onEdit={() => {
                  setEditTask(task);
                  setTaskDialog(true);
                }}
                onDelete={() => handleDelete(task.pm_id)}
                onCreateTicket={() => {
                  setActiveTask(task);
                  setTicketDialog(true);
                }}
                onApprove={handleApprove}
                onComplete={handleComplete}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* ── Dialogs ── */}
      <TaskDialog
        open={taskDialog}
        onClose={() => {
          setTaskDialog(false);
          setEditTask(null);
        }}
        editData={editTask}
        projects={projects}
        allUsers={allUsers}
        userInfo={userInfo}
        onSaved={handleTaskSaved}
        showSnack={showSnack}
        selectedProject={selectedProject}
        initialScreenName={persistedScreenName}
        onScreenNameChange={(newName) => {
          setPersistedScreenName(newName);
          setPersistedProjectId(selectedProject?.id);
        }}
      />

      <TicketFormDialog
        open={ticketDialog}
        onClose={() => {
          setTicketDialog(false);
          setActiveTask(null);
        }}
        pmTask={activeTask}
        userInfo={userInfo}
        onCreated={handleTicketCreated}
        showSnack={showSnack}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={2500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.sev}
          variant="filled"
          sx={{ borderRadius: "10px" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProjectManagement;
